import { Colors, DropdownSelect } from "@factorearth/component-library";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { binarySearch } from "utils/binarySearch";
import { sortByAlphaNumericalArray } from "utils/sort";
import styles from "./ProjectList.module.scss";
import { Project, ProjectForResolver } from "db/definitions";
import { RecentProject, updateRecentProjects } from "utils/localstorage/projects";
import RestoreProjectFromJson from "components/RestoreProjectFromJson/RestoreProjectFromJson";

interface Props {
	color: Colors;
	getProjects?: () => Promise<void>;
	handleChangeProject: (e: { target: { label?: string; value: string } }) => void;
	header?: JSX.Element;
	navMenu?: boolean;
	online: boolean;
	projectId: string;
	projects: Project[] | ProjectForResolver[];
	recentProjects: RecentProject[];
	setCreateProjectOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

function ProjectList(props: Props) {
	const { color, getProjects, handleChangeProject, header, navMenu, online, projectId, projects, recentProjects, setCreateProjectOpen } = props;

	const [selectedProject, setSelectedProject] = useState<ProjectForResolver | null>(null);
	const [restoreProjectJsonOpen, setRestoreProjectJsonOpen] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	const syncMenu = location.pathname.includes("sync-menu");
	const templateManager = location.pathname.includes("template-manager");

	useEffect(() => {
		if (projectId) {
			const project = projects.find((project) => project.id === projectId);
			setSelectedProject(project || null);
		}
	}, [projectId]);

	const sortedProjects = useMemo(() => {
		let projectsCopy = [...projects];
		if (online && !syncMenu && !templateManager) {
			// For keeping things simple with regards to having all things in the sync menu, we fetch all online and offline when the user is online. Here, we need to filter out
			// their offline projects so they can't select them
			projectsCopy = projectsCopy.filter((proj) => {
				if ("offline" in proj) {
					return !proj.offline;
				}
				return true;
			});
		}
		const map: Record<string, ProjectForResolver[]> = {};
		for (const project of projectsCopy) {
			if (!map[project.group]) {
				map[project.group] = [project];
			} else {
				const [alreadyInArray, index] = binarySearch(map[project.group], project, alphaNum);
				if (!alreadyInArray) {
					map[project.group].splice(index, 0, project);
				}
			}
		}
		function alphaNum(projectWeLookFor: ProjectForResolver, projectAlreadyinArray: ProjectForResolver) {
			const nameWeSearchFor = projectWeLookFor.name.toUpperCase();
			const nameAlreadyInArray = projectAlreadyinArray.name.toUpperCase();
			if (nameWeSearchFor === nameAlreadyInArray) {
				return 1;
			}
			const stringToCheck = nameWeSearchFor.length > nameAlreadyInArray.length ? nameWeSearchFor : nameAlreadyInArray;
			for (let i = 0; i < stringToCheck.length; i++) {
				const charA = nameWeSearchFor[i];
				const charB = nameAlreadyInArray[i];
				const aIsString = isNaN(parseInt(charA));
				const bIsString = isNaN(parseInt(charB));
				if (charA !== charB) {
					if ((aIsString && bIsString) || (!aIsString && !bIsString)) {
						return charA > charB ? 1 : -1;
					} else if (aIsString && !bIsString) {
						return -1;
					} else if (!aIsString && bIsString) {
						return 1;
					}
				}
				if (i === stringToCheck.length - 1) {
					//For it to get here, we're on the last string of char
					return 1;
				}
			}
			if (nameWeSearchFor.length === nameAlreadyInArray.length) return 0;
			return nameWeSearchFor.length > nameAlreadyInArray.length ? 1 : -1;
		}
		// sorted groups basically
		const keys = sortByAlphaNumericalArray({ array: Object.keys(map) });
		const finalList = [];
		const none = {
			label: "None",
			value: "",
		};
		const createProjectOption = {
			label: "Create New Project",
			value: "CREATE_NEW_PROJECT",
		};
		const restoreProjectOption = {
			label: "Restore Project",
			value: "RESTORE_PROJECT",
		};
		const recentProjectsGroup = {
			label: "RECENT PROJECTS",
			options: recentProjects.map((proj) => ({
				label: proj.name,
				value: proj.id,
			})),
		};

		finalList.push(none);
		if (!syncMenu && !templateManager) {
			finalList.push(createProjectOption);
			finalList.push(restoreProjectOption);
		}
		finalList.push(recentProjectsGroup);

		for (const key of keys) {
			const groupOption = {
				label: key,
				options: [] as { label: string; value: string }[],
			};

			const group = sortByAlphaNumericalArray({ array: map[key], key: "name" });
			for (const proj of group) {
				const projObj = {
					label: proj.name,
					value: proj.id,
				};

				groupOption.options.push(projObj);
			}

			finalList.push(groupOption);
		}
		return finalList;
	}, [online, projects]);

	function selectHandleChangeProject(data: { label: string; value: string }) {
		if (data.value === "CREATE_NEW_PROJECT") {
			setCreateProjectOpen?.(true);
		} else if (data.value === "RESTORE_PROJECT") {
			setRestoreProjectJsonOpen(true);
		} else {
			const changeData = {
				target: {
					value: data.value,
				},
			};

			handleChangeProject(changeData);

			if ((!syncMenu && !templateManager) || navMenu) {
				navigate(`/project/${data.value}`);
			}
			const project = projects.find((project) => project.id === data.value);
			if (project) updateRecentProjects({ id: project.id, name: project.name, description: project.description, group: project.group } as RecentProject);
		}
	}

	/**
	 * setup the selected string value into objects for the dropdown
	 * @returns selected values as dropdown option objects
	 */
	function buildValue() {
		if (!projectId || !selectedProject) {
			return "";
		}

		const value = {
			value: projectId,
			label: selectedProject.name,
		};
		return value;
	}

	return (
		<div
			className={templateManager ? styles.templateManagerProjectList : styles.wrapper}
			key={"project-list-wrapper"}
			style={(!syncMenu && !templateManager) || navMenu ? { padding: "16px 24px" } : {}}>
			{getProjects && (
				<RestoreProjectFromJson
					colors={color}
					getProjects={getProjects}
					online={online}
					open={restoreProjectJsonOpen}
					projects={projects}
					setOpen={setRestoreProjectJsonOpen}
				/>
			)}
			{header}
			<DropdownSelect
				data-test="project-list-searchable"
				name={"Project List"}
				id={`project-list-searchable-${navMenu ? "nav" : "main"}`}
				value={buildValue()}
				// @ts-ignore
				onChange={selectHandleChangeProject}
				options={sortedProjects}
				isSearchable={true}
				menuPlacement={navMenu ? "auto" : "top"}
				minMenuHeight={300}
				menuPortalTarget={document.body}
				classNamePrefix={"react-select"}
				colorPalette={color}
			/>
		</div>
	);
}

export default ProjectList;
